import { useEffect } from 'react';
import { gql, useQuery } from '@apollo/client';
import { trackAccessEventOnce } from '@arthelper/shared/facebook-pixel';
import { arrivalConversion, waitingDoneConversions } from '@arthelper/shared/acquisition/access-conversions';
import { decodeReferralHash } from '@arthelper/shared/referral-hash';
import { WAITLIST_PATH } from '@arthelper/shared/acquisition/strategy';
import { DEFAULT_TOWN_SQUARE_URL } from '~/utils/townSquareLinks';

const MY_ACCESS = gql`
  query WelcomeMyAccess {
    myAccess {
      state
      via
      copy
      referralHash
      joinedAt
      isTargetArtist
      inviter {
        name
      }
      waitlist {
        requiredReferrals
        qualifiedCount
        waitingWizard
      }
    }
  }
`;

interface WaitingAccess {
  state: string;
  /** How they got in: open, invite (a friend's link), referral_unlock or admin_release. */
  via: string | null;
  copy: Record<string, string>;
  referralHash: string;
  joinedAt: string | null;
  /** A committed maker in a core market: finishing the waiting wizard also counts as AddToWishlist. */
  isTargetArtist: boolean;
  inviter: { name: string } | null;
  waitlist: { requiredReferrals: number; qualifiedCount: number; waitingWizard: string | null } | null;
}

export function townSquareUrl(path: string): string {
  return `${(__TOWN_SQUARE_URL__ || DEFAULT_TOWN_SQUARE_URL).replace(/\/$/, '')}${path}`;
}

export const WAITLIST_URL = (): string => townSquareUrl(WAITLIST_PATH);

/**
 * Whether the member in the wizard is still held behind the signup gate — the waiting wizard
 * (acquisition strategies): they build a private profile while they wait, and finish on a
 * "we'll let you know" screen instead of the live-profile Done. `allowed` is false for a held
 * member whose strategy has no waiting wizard; they belong on the waitlist page.
 */
export function useWaiting({ skip = false }: { skip?: boolean } = {}): {
  loading: boolean;
  waiting: boolean;
  allowed: boolean;
  access: WaitingAccess | null;
} {
  const { data, loading } = useQuery<{ myAccess: WaitingAccess | null }>(MY_ACCESS, {
    fetchPolicy: 'network-only',
    skip,
  });
  const access = data?.myAccess ?? null;
  const waiting = access?.state === 'waitlisted';
  return { loading, waiting, allowed: !waiting || access?.waitlist?.waitingWizard != null, access };
}

/**
 * The browser half of the acquisition Meta conversions the wizard can see (the API sends each
 * with the same id): `Contact` for a held member, `StartTrial` for one recently let in.
 */
export function useAccessConversions(access: WaitingAccess | null): void {
  useEffect(() => {
    const asfId = access ? decodeReferralHash(access.referralHash) : null;
    const event = access ? arrivalConversion(access) : null;
    if (asfId === null || !event) return;
    trackAccessEventOnce(event, asfId);
  }, [access]);
}

/**
 * The browser half of the waiting wizard's finish: `Schedule`, plus `AddToWishlist` for our target
 * audience. It reads access fresh: the wizard's own copy predates the career and frequency answers.
 */
export function useWaitingDoneConversions(): void {
  const { data } = useQuery<{ myAccess: WaitingAccess | null }>(MY_ACCESS, { fetchPolicy: 'network-only' });
  const access = data?.myAccess ?? null;
  useEffect(() => {
    const asfId = access ? decodeReferralHash(access.referralHash) : null;
    if (!access || asfId === null) return;
    waitingDoneConversions(access.isTargetArtist).forEach((event) => trackAccessEventOnce(event, asfId));
  }, [access]);
}
